/**
 * CompanyUserDefaultControl — inline editor for a company's DEFAULT per-user
 * monthly Eva cap (SF-3850).
 *
 * The per-company OVERALL cap is edited by CompanyBudgetControl (SF-3737) and
 * an INDIVIDUAL user's cap by UserBudgetEditor (SF-3738). This card sits
 * between them: it sets the DEFAULT per-user cap applied to EVERY user in the
 * company who lacks an individual override. In the chat-service cascade it
 * overrides the global per-user default (SF-3839) but is itself overridden by
 * an individual per-user override.
 *
 * Surfaces the current effective default per-user cap, an editable numeric
 * input, and a Save button that only appears once the value differs from the
 * loaded value. A "Revert to global default" action clears the per-company
 * default (sends null) when one is set.
 *
 * Permission-aware: gated on the SAME admin:eva:budget:{read,write} scope as
 * the per-company override. Read-only operators see a disabled input + a
 * tooltip; a server-side 403 (defence in depth) surfaces a clear error toast.
 */
import { useEffect, useMemo, useState } from 'react';
import { usePermissions } from '@refinedev/core';
import { Button, Card, InputNumber, Skeleton, Space, Tooltip, Typography, message } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import type { AxiosError } from 'axios';

import { hasPermission } from '../../utils/permissions';
import { useCanEditCompany } from '../companies/api/editableCompaniesClient';
import { formatCurrency, EM_DASH, parseMoney } from '../../utils/format';
import {
  EVA_BUDGET_READ_PERMISSION,
  EVA_BUDGET_WRITE_PERMISSION,
  companyUserDefaultMutationArgs,
  useCompanyUserDefault,
  useSetCompanyUserDefault,
  type EvaCompanyUserDefault,
} from './api/evaCompanyUserDefaultClient';

const { Text } = Typography;

export interface CompanyUserDefaultControlProps {
  /** Company ObjectId whose default per-user cap is being edited. */
  companyId: string;
  /** Gate the read request on access-control resolution (defaults to true). */
  enabled?: boolean;
  /**
   * Called after a successful save so the parent can refetch any dependent
   * figures (e.g. per-user usage tiles).
   */
  onSaved?: () => void;
}

/** Pull a human-readable message out of an sf-api axios error (incl. 403). */
function errorMessage(error: unknown, fallback: string): string {
  const axiosErr = error as AxiosError<{ message?: string }> | undefined;
  if (axiosErr?.response?.status === 403) {
    return 'You do not have permission to change this company\u2019s default per-user Eva budget.';
  }
  return axiosErr?.response?.data?.message || fallback;
}

/** Inline-editable DEFAULT per-user monthly Eva cap for a single company. */
export const CompanyUserDefaultControl: React.FC<CompanyUserDefaultControlProps> = ({
  companyId,
  enabled = true,
  onSaved,
}) => {
  // Belt-and-braces UX: hide the write affordances when the operator lacks the
  // write permission. The server enforces this too (403) — see errorMessage.
  const { data: permissions, isLoading: permsLoading } = usePermissions<string[]>();
  const perms = permissions ?? [];
  const canRead = hasPermission(perms, EVA_BUDGET_READ_PERMISSION);
  // SF-4668: staff with a company list may only change the companies on it.
  const { canEditCompany } = useCanEditCompany(companyId);
  const canWrite = hasPermission(perms, EVA_BUDGET_WRITE_PERMISSION) && canEditCompany;

  // Only fire the GET when the operator actually holds budget:read. The
  // endpoint is gated on admin:eva:budget:read independently of usage:read, so
  // an operator who can see Eva usage but lacks budget:read would otherwise hit
  // a silent 403 and see an unexplained empty card. Gating the request lets us
  // render an explicit "no access" state. Wait for permissions to resolve so we
  // don't fire a doomed request (or flash the denial) during the brief load.
  const defaultQuery = useCompanyUserDefault(companyId, {
    enabled: enabled && !permsLoading && canRead,
  });
  const companyDefault: EvaCompanyUserDefault | undefined = defaultQuery.data?.data;
  const setDefault = useSetCompanyUserDefault();

  // The default per-user cap currently enforced (per-company value or global
  // default) and whether a per-company default is set. Null-guarded.
  const effectiveCap = parseMoney(companyDefault?.effectiveDefaultUserCapUsd);
  const isCompanyDefault = companyDefault?.isCompanyDefault === true;

  // Local edit state, seeded from the loaded effective cap. `null` is valid
  // while the field is empty; Save only enables when it actually moves.
  const [value, setValue] = useState<number | null>(null);

  // Re-seed the input whenever fresh data lands (initial load or post-save
  // refetch) so the "dirty" comparison below is against the persisted value.
  useEffect(() => {
    setValue(effectiveCap);
    // companyDefault identity changes on every (re)fetch; effectiveCap derives from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDefault]);

  // Save only appears/enables when the edited value differs from what loaded.
  const isDirty = useMemo(() => value !== effectiveCap, [value, effectiveCap]);

  const saving = setDefault.isLoading;

  const handleSave = () => {
    // Guard: a STRICTLY POSITIVE finite number is required to upsert. This
    // mirrors the sf-api writer (`> 0`, returns 400 on 0) and the chat-service
    // reader, which honours a published cap only when it parses to `> 0` — a 0
    // would be silently ignored and fall through to the global default. To
    // remove this company default, revert to the global default (sends null).
    if (value === null || !Number.isFinite(value) || value <= 0) {
      message.error('Enter a positive default per-user cap, or revert to the global default.');
      return;
    }
    setDefault.mutate(companyUserDefaultMutationArgs(companyId, { defaultUserCapUsd: value }), {
      onSuccess: () => {
        message.success('Company default per-user Eva budget updated.');
        // Refetch this control's data so isCompanyDefault/effectiveCap reflect
        // the write, and let the parent refresh any dependent figures.
        defaultQuery.refetch?.();
        onSaved?.();
      },
      onError: (error) => {
        message.error(errorMessage(error, 'Failed to update the default per-user Eva budget.'));
      },
    });
  };

  const handleRevert = () => {
    // null → delete the per-company default on sf-api, reverting to the global
    // per-user default (SF-3839).
    setDefault.mutate(companyUserDefaultMutationArgs(companyId, { defaultUserCapUsd: null }), {
      onSuccess: () => {
        message.success('Reverted to the global default per-user Eva budget.');
        defaultQuery.refetch?.();
        onSaved?.();
      },
      onError: (error) => {
        message.error(errorMessage(error, 'Failed to revert the default per-user Eva budget.'));
      },
    });
  };

  const writeDisabledHint = canWrite
    ? undefined
    : !canEditCompany
      ? 'This company is not on your list of companies, so you can only view it.'
      : 'You do not have permission to change the Eva budget (admin:eva:budget:write).';

  return (
    <Card style={{ height: '100%' }}>
      {permsLoading ? (
        // Permissions still resolving — show the skeleton, not a false "no
        // access" denial, until we actually know the operator's grants.
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: '40%' }} />
      ) : !canRead ? (
        // Budget read is gated independently of usage read — be explicit rather
        // than rendering a blank/empty card when the operator can't see it.
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text type="secondary">
            <TeamOutlined /> Company Default Per-User Eva Budget
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            You do not have permission to view the Eva budget (admin:eva:budget:read).
          </Text>
        </Space>
      ) : defaultQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: '40%' }} />
      ) : defaultQuery.isError ? (
        // Surface a load failure inline (the query suppresses the global toast).
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text type="secondary">
            <TeamOutlined /> Company Default Per-User Eva Budget
          </Text>
          <Text type="danger" style={{ fontSize: 12 }}>
            Could not load the default per-user Eva budget. Please retry.
          </Text>
          <Button size="small" onClick={() => defaultQuery.refetch?.()}>
            Retry
          </Button>
        </Space>
      ) : (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text type="secondary">
            <TeamOutlined /> Company Default Per-User Eva Budget
          </Text>
          <Space wrap align="center" size="middle">
            <Tooltip title={writeDisabledHint}>
              {/* AntD tooltips don't fire on a disabled control — the disabled
                  element swallows pointer events. Wrap it in an inline-block
                  span (only when disabled) so the hint is hoverable for
                  read-only operators; when enabled, render the input directly
                  to keep the normal layout/focus behaviour. */}
              {!canWrite ? (
                <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
                  <InputNumber
                    aria-label="Company default per-user Eva budget (USD)"
                    value={value}
                    min={0.01}
                    step={5}
                    disabled
                    prefix="$"
                    style={{ width: 200, pointerEvents: 'none' }}
                    onChange={(next) => setValue(typeof next === 'number' ? next : null)}
                  />
                </span>
              ) : (
                <InputNumber
                  aria-label="Company default per-user Eva budget (USD)"
                  value={value}
                  min={0.01}
                  step={5}
                  disabled={saving}
                  prefix="$"
                  style={{ width: 200 }}
                  onChange={(next) => setValue(typeof next === 'number' ? next : null)}
                />
              )}
            </Tooltip>
            {/* Save only appears once the value differs from the loaded cap. */}
            {canWrite && isDirty && (
              <Button type="primary" loading={saving} onClick={handleSave}>
                Save
              </Button>
            )}
            {/* Revert is offered only when a per-company default is actually set. */}
            {canWrite && isCompanyDefault && (
              <Button danger loading={saving} onClick={handleRevert}>
                Revert to global default
              </Button>
            )}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {effectiveCap !== null
              ? `Effective default per-user cap: ${formatCurrency(effectiveCap)} / month${
                  isCompanyDefault ? ' (per-company default)' : ' (global default)'
                }. Individual user overrides take precedence.`
              : EM_DASH}
          </Text>
        </Space>
      )}
    </Card>
  );
};
